/**
 * DemocratCharts — indicator state.
 *
 * Each indicator has its own color, line width, and line style — fully user
 * customisable. Persisted to localStorage.
 */

import { create } from "zustand";

export type IndicatorType = "ema" | "sma" | "vwap" | "bb" | "mfi" | "moon";
export type LineStyle = "solid" | "dashed" | "dotted";
export type LineWidth = 1 | 2 | 3;

export interface IndicatorConfig {
  id: string;
  type: IndicatorType;
  params: Record<string, number>;
  color: string;
  lineWidth: LineWidth;
  lineStyle: LineStyle;
  enabled: boolean;
}

const LS_KEY = "livecharts:indicators:v3"; // bumped: BB multiplier renamed to deviation, MFI/moon added

// Tasteful default palette for *auto-assigned* colors when adding a new
// indicator. User can override via ColorPicker. Avoids reds (used for SL)
// and bright greens (TP).
export const INDICATOR_AUTO_COLORS = [
  "#ffb74d", // amber
  "#9575cd", // purple
  "#4fc3f7", // sky blue
  "#aed581", // light green (not the bright bull green)
  "#f06292", // pink
  "#ff8a65", // coral
  "#00bcd4", // cyan
];

function loadIndicators(): IndicatorConfig[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as IndicatorConfig[];
  } catch {
    /* ignore */
  }
  return [];
}

function saveIndicators(list: IndicatorConfig[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

function defaultParamsFor(type: IndicatorType): Record<string, number> {
  switch (type) {
    case "ema":
    case "sma":
      return { period: 21 };
    case "vwap":
      return {};
    case "bb":
      return { period: 20, deviation: 2 };
    case "mfi":
      return { period: 14 };
    case "moon":
      return {};
  }
}

function defaultStyleFor(type: IndicatorType): LineStyle {
  return "solid";
}

export function labelFor(config: Pick<IndicatorConfig, "type" | "params">): string {
  switch (config.type) {
    case "ema":  return `EMA ${config.params.period}`;
    case "sma":  return `SMA ${config.params.period}`;
    case "vwap": return "VWAP";
    case "bb":   return `Bollinger Bands (${config.params.period}, ${config.params.deviation}σ)`;
    case "mfi":  return `MFI ${config.params.period}`;
    case "moon": return "Moon Phases";
  }
}

/** Indicators that live in a separate pane below the main chart. */
export function isSubPaneIndicator(type: IndicatorType): boolean {
  return type === "mfi";
}

/** Indicators rendered as markers on the candle series rather than lines. */
export function isMarkerIndicator(type: IndicatorType): boolean {
  return type === "moon";
}

interface IndicatorsState {
  list: IndicatorConfig[];
  add: (type: IndicatorType, overrides?: Partial<IndicatorConfig>) => void;
  remove: (id: string) => void;
  toggle: (id: string) => void;
  updateParams: (id: string, params: Record<string, number>) => void;
  updateColor: (id: string, color: string) => void;
  updateLineWidth: (id: string, width: LineWidth) => void;
  updateLineStyle: (id: string, style: LineStyle) => void;
}

export const useIndicatorsStore = create<IndicatorsState>((set, get) => ({
  list: loadIndicators(),

  add: (type, overrides = {}) => {
    const existing = get().list;
    const usedColors = new Set(existing.map((i) => i.color));
    const color =
      overrides.color ??
      INDICATOR_AUTO_COLORS.find((c) => !usedColors.has(c)) ??
      INDICATOR_AUTO_COLORS[existing.length % INDICATOR_AUTO_COLORS.length];

    const item: IndicatorConfig = {
      id: `ind_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      type,
      params: { ...defaultParamsFor(type), ...(overrides.params ?? {}) },
      color,
      lineWidth: overrides.lineWidth ?? 2,
      lineStyle: overrides.lineStyle ?? defaultStyleFor(type),
      enabled: true,
    };
    const next = [...existing, item];
    saveIndicators(next);
    set({ list: next });
  },

  remove: (id) => {
    const next = get().list.filter((i) => i.id !== id);
    saveIndicators(next);
    set({ list: next });
  },

  toggle: (id) => {
    const next = get().list.map((i) => (i.id === id ? { ...i, enabled: !i.enabled } : i));
    saveIndicators(next);
    set({ list: next });
  },

  updateParams: (id, params) => {
    const next = get().list.map((i) =>
      i.id === id ? { ...i, params: { ...i.params, ...params } } : i
    );
    saveIndicators(next);
    set({ list: next });
  },

  updateColor: (id, color) => {
    const next = get().list.map((i) => (i.id === id ? { ...i, color } : i));
    saveIndicators(next);
    set({ list: next });
  },

  updateLineWidth: (id, width) => {
    const next = get().list.map((i) => (i.id === id ? { ...i, lineWidth: width } : i));
    saveIndicators(next);
    set({ list: next });
  },

  updateLineStyle: (id, style) => {
    const next = get().list.map((i) => (i.id === id ? { ...i, lineStyle: style } : i));
    saveIndicators(next);
    set({ list: next });
  },
}));
